import { colorTextAction } from '@calendly/ui/theme'

import {
  applyFixedMeetingsOrganizerScope,
  DEFAULT_SUPERGRAPH_MEETING_KINDS,
} from 'features/meetings/hooks/search_data/supergraph_meeting_search_utils'
import { INVALID_SUPERGRAPH_SCOPE_TYPES } from 'features/meetings/supergraph/scope_utils'
import { mapNodeToMeeting } from 'features/meetings/utils'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

import { MATCH_ATTENDEE, MATCH_MEETING, MATCH_TRANSCRIPT } from './constants'

const MEETINGS_MEETINGS_FILTER_WHITELIST = new Set([
  'attendee_emails',
  'cancelled',
  'end_time',
  'event_type_uuids',
  'has_mi',
  'ids',
  'include_notetaker_ineligible_external_events',
  'invitee_emails',
  'is_organizer',
  'kinds',
  'location_kind',
  'meeting_intelligence_status',
  'organization_uuid',
  'owner_type',
  'role',
  'search_keyword',
  'scope_id',
  'scope_type',
  'start_time',
  'status',
  'statuses',
  'team_uuids',
  'tracking_ids',
  'user_lens',
  'user_uuids',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
  'uuid',
])

const pickMeetingsMeetingsFilter = (raw) => {
  if (!raw || typeof raw !== 'object') return {}
  const out = {}
  for (const key of Object.keys(raw)) {
    if (MEETINGS_MEETINGS_FILTER_WHITELIST.has(key)) {
      out[key] = raw[key]
    }
  }
  return out
}

/**
 * @param {string} searchKeyword - from URL/search UI. Sent on the `meetings_search` variable as `search_keyword` only.
 */
export const buildMeetingsSearchSupergraphInput = ({
  searchKeyword,
  sortBy,
  preparedFilter,
  scopeType,
  scopeId,
  organizationUuid,
}) => {
  const filter = {}

  if (organizationUuid != null && organizationUuid !== '') {
    filter.organization_uuid = String(organizationUuid)
  }

  const q = typeof searchKeyword === 'string' ? searchKeyword.trim() : ''
  const defaultSearchIn = [MATCH_ATTENDEE, MATCH_TRANSCRIPT, MATCH_MEETING]
  const searchIn = (preparedFilter?.searchIn?.length
    ? preparedFilter.searchIn
    : defaultSearchIn)

  if (preparedFilter?.inviteeEmails?.length) {
    filter.invitee_emails = preparedFilter.inviteeEmails
  }
  if (preparedFilter?.eventTypeUuids?.length) {
    filter.event_type_uuids = preparedFilter.eventTypeUuids
  }
  if (preparedFilter?.kinds?.length) {
    filter.kinds = preparedFilter.kinds
  } else {
    filter.kinds = [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (preparedFilter?.role?.length) {
    filter.role = preparedFilter.role
  }

  if (preparedFilter?.statuses?.length === 1) {
    filter.status = preparedFilter.statuses[0]
  } else if (preparedFilter?.statuses?.length > 1) {
    filter.statuses = preparedFilter.statuses
  }

  if (preparedFilter?.startTimeRange) {
    const { operation, start, end } = preparedFilter.startTimeRange
    filter.start_time = { operation, start, end }
  }

  if (preparedFilter?.userIds?.length) {
    filter.user_uuids = preparedFilter.userIds
  }
  if (preparedFilter?.teamIds?.length) {
    filter.team_uuids = preparedFilter.teamIds
  }

  const mappedScope = mapScopeTypeToBackendValue(scopeType, scopeId)

  if (mappedScope === 'all_users_and_teams') {
    if (scopeId) filter.scope_id = scopeId
  } else if (mappedScope && !INVALID_SUPERGRAPH_SCOPE_TYPES.has(mappedScope)) {
    filter.scope_type = mappedScope
    if (scopeId && mappedScope !== 'my_calendly' && scopeId !== 'me') {
      filter.scope_id = scopeId
    }
  }

  applyFixedMeetingsOrganizerScope(filter)

  return {
    search_keyword: q,
    findBestCharForPassages: true,
    orderBy: { prioritize: sortBy },
    searchIn,
    filter: pickMeetingsMeetingsFilter(filter),
  }
}

export const convertSupergraphSearchResponse = (response, timezone, hasMsTeamsFlag = false) => {
  if (!response?.meetings?.edges) return null

  const { meetings } = response
  const data = meetings.edges.map(({ node }) => {
    const mapped = mapNodeToMeeting(node, timezone, hasMsTeamsFlag)
    return {
      ...mapped,
      color: mapped.eventType?.color || colorTextAction,
      attendees: (mapped.attendees || []).filter(({ kind }) => kind === 'INVITEE'),
      guests: (mapped.attendees || []).filter(({ kind }) => kind === 'EXTERNAL'),
      transcriptMatches: [],
      hasMeetingIntelligence: Boolean(mapped.meetingIntelligence?.uuid),
      trackedMeetingUuid: mapped.meetingIntelligence?.uuid,
    }
  })

  return {
    totalSize: meetings.total,
    data,
  }
}
